import { dataService, fileService as file } from 'platform/adapter/persistence/service'
import { now } from 'platform/adapter/persistence/helper'
import descriptor from 'platform/adapter/persistence/descriptor/files'
import persistence from 'platform/adapter/persistence/helper/persistence'
import settings from '@tenant/settings'

/**
 * @memberOf Gaia.Adapter.Persistence.PersistenceAPI
 * @typedef {PersistenceAPI} FilesAPI
 * @returns {FilesAPI}
 */
export default (() => {
  const fileData = dataService(descriptor)
  const folderName = descriptor.name

  return Object.create(persistence, {
    init: {
      /**
       * Creates the file store, if it doesn't exist.
       *
       * @returns {Promise<void>}
       */
      value: async () => await fileData.init(),
      iterable: true,
      enumerable: true,
    },
    get: {
      /**
       * Attempts to obtain a file stored with `put`.
       *
       * @param {string} url      the URL the file was obtained from, which is also its identifier
       * @returns {Promise<Blob>}  the contents of the file
       */
      value: async (url) => {
        const item = await fileData.get(url)
        return await file.get(item, folderName)
      },
      iterable: true,
      enumerable: true,
    },
    put: {
      /**
       * Attempts to store a new file.
       *
       * @param {Object} item
       * @param {string} item.url     the URL the file was obtained from, which is its identifier
       * @param {string} item.name    the name of the file
       * @param {Blob} item.contents  the contents of the file
       * @returns {Promise<void>}    the identifier of the new persisted item
       */
      value: async ({ url, name, contents }) => {
        const item = { url, name, contents, date: now() }
        const { size } = contents
        const limit = settings.maximumPersistedFileBytes

        if (!limit || size <= limit) {
          await fileData.put(item)
          try {
            return await file.put(item, folderName)
          } catch (error) {
            await fileData.remove(url)
            throw error
          }
        }

        console.log(`File "${name}" size exceeded soft limit (${size} B > ${limit} B) and was not saved.`)
        return Promise.resolve()
      },
      iterable: true,
      enumerable: true,
    },
    delete: {
      /**
       * Attempts to delete a file stored with `put`.
       *
       * @param {string} url      the URL the file was obtained from, which is its identifier
       * @returns {Promise<void>}  the identifier of the new persisted item
       */
      value: async (url) => {
        const item = await fileData.get(url)
        await fileData.remove(url)
        return await file.remove(item, folderName)
      },
      iterable: true,
      enumerable: true,
    },
    deleteAll: {
      /**
       * Attempts to delete all files stored by this api.
       *
       * @returns {Promise<void>}
       */
      value: async () => {
        const items = await fileData.getAll()
        await items.reduce(async (promise, item) => {
          await promise
          try {
            await file.remove(item, folderName)
            return fileData.remove(item.url)
          } catch (e) {
            console.log(e)
            return Promise.resolve()
          }
        }, Promise.resolve())
      },
    },
    clean: {
      /**
       * Attempts to delete all files whose create `date` exceeds the `maximumPersistenceDays`
       * setting.
       *
       * @returns {Promise<void>}
       */
      value: async () => {
        const daysLimit = settings.maximumPersistenceDays
        if (daysLimit) {
          // removing old entries
          const date = new Date()
          date.setDate(date.getDate() - daysLimit)
          date.setHours(0, 0, 0, 0)
          const iso = date.toISOString()
          const files = await fileData.getAll({ date: ['<', iso] })
          await files.reduce(async (promise, oldFile) => {
            await promise
            try {
              await file.remove(oldFile, folderName)
              return await fileData.remove(oldFile.url)
            } catch (e) {
              console.log(e)
              return Promise.resolve()
            }
          }, Promise.resolve())
        }
        return null
      },
    },
  })
})()
